// src/services/wirdRatingsService.js
//
// Firestore service for the `wirdRatings` collection defined in
// Merouani1.md §1.1. Each Save creates a new immutable doc so the full
// history is preserved; no overwrites.
//
// Doc shape (canonical):
//   studentId, classId, teacherId,
//   date: 'YYYY-MM-DD',
//   savedAt: Timestamp,
//   kind: 'new' | 'revision' | 'custom',
//   portion: {
//     mode: 'rubuu' | 'custom',
//     hizb: number, rubuu: number | null,
//     verses: [{ surah, from, to }]
//   },
//   hifzScore: 0..100 int,
//   tajweedScore: 0..100 int,
//   tajweedRemarks: [{ key, state: 'neutral'|'red'|'green' }],
//   audioNoteUrl, audioDurationMs,
//   notes
//
// Compound indexes expected (see firestore.indexes.json):
//   (classId, date desc), (studentId, date desc)

import {
  collection,
  addDoc,
  getDoc,
  getDocs,
  doc,
  query,
  where,
  orderBy,
  limit as fsLimit,
  Timestamp,
  serverTimestamp,
} from 'firebase/firestore';
import { db } from '../firebase/config';
import {
  detectMemorizationSuggestions,
  upsertSuggestion,
  toPortionId,
} from './memorizationSuggestionsService';
import { getHafizRules } from './hafizRulesService';
import { srsService } from './srsService';

// Map a 0..100 hifz score to FSRS quality (1..5).
// The cutoffs match the labels the slider already shows ("ممتاز / جيد جدا /
// متوسط / ضعيف"): only saves with hifz < 40 trigger an FSRS lapse.
function hifzScoreToQuality(score) {
  const n = Number(score);
  if (!Number.isFinite(n)) return 3;
  if (n >= 90) return 5;
  if (n >= 75) return 4;
  if (n >= 60) return 3;
  if (n >= 40) return 2;
  return 1;
}

export const WIRD_RATINGS = 'wirdRatings';

export const WIRD_KINDS = ['new', 'revision', 'custom'];
export const PORTION_MODES = ['rubuu', 'custom'];
export const REMARK_STATES = ['neutral', 'red', 'green'];

function clampScore(n) {
  const x = Math.round(Number(n));
  if (Number.isNaN(x)) return 0;
  return Math.max(0, Math.min(100, x));
}

function normalizeVerses(verses) {
  if (!Array.isArray(verses)) return [];
  return verses
    .map((v) => ({
      surah: Number(v.surah),
      from: Number(v.from),
      to: Number(v.to),
    }))
    .filter((v) => Number.isFinite(v.surah) && Number.isFinite(v.from) && Number.isFinite(v.to));
}

function normalizeRemarks(remarks) {
  if (!Array.isArray(remarks)) return [];
  return remarks
    .map((r) => ({
      key: String(r.key || '').trim(),
      state: REMARK_STATES.includes(r.state) ? r.state : 'neutral',
    }))
    .filter((r) => r.key);
}

/**
 * Validate + normalize a wird rating payload before write.
 * Throws a descriptive Error if the payload is invalid.
 */
export function normalizeWirdRating(input) {
  if (!input || typeof input !== 'object') {
    throw new Error('wirdRating payload must be an object');
  }
  const { studentId, classId, teacherId, date, kind, portion } = input;
  if (!studentId) throw new Error('studentId is required');
  if (!classId) throw new Error('classId is required');
  if (!teacherId) throw new Error('teacherId is required');
  if (!/^\d{4}-\d{2}-\d{2}$/.test(date || '')) {
    throw new Error(`date must be YYYY-MM-DD, got "${date}"`);
  }
  if (!WIRD_KINDS.includes(kind)) {
    throw new Error(`kind must be one of ${WIRD_KINDS.join(',')}, got "${kind}"`);
  }
  if (!portion || !PORTION_MODES.includes(portion.mode)) {
    throw new Error(`portion.mode must be one of ${PORTION_MODES.join(',')}`);
  }
  const hizb = Number(portion.hizb);
  if (!Number.isInteger(hizb) || hizb < 1 || hizb > 60) {
    throw new Error(`portion.hizb must be integer in [1,60], got ${portion.hizb}`);
  }
  const rubuu = portion.rubuu == null ? null : Number(portion.rubuu);
  if (rubuu != null && (!Number.isInteger(rubuu) || rubuu < 1 || rubuu > 4)) {
    throw new Error(`portion.rubuu must be null or integer in [1,4], got ${portion.rubuu}`);
  }

  return {
    studentId: String(studentId),
    classId: String(classId),
    teacherId: String(teacherId),
    // tenantId is required by firestore.rules incomingSameTenant() — the
    // caller resolves it from AuthContext / tenantContext and passes it
    // in. We coerce-or-null here rather than throwing so existing tests
    // that don't supply it (the rules envelope is mocked) still pass.
    tenantId: input.tenantId ? String(input.tenantId) : null,
    date,
    kind,
    portion: {
      mode: portion.mode,
      hizb,
      rubuu,
      verses: normalizeVerses(portion.verses),
    },
    hifzScore: clampScore(input.hifzScore),
    tajweedScore: clampScore(input.tajweedScore),
    tajweedRemarks: normalizeRemarks(input.tajweedRemarks),
    audioNoteUrl: input.audioNoteUrl || null,
    audioDurationMs: input.audioDurationMs == null ? null : Number(input.audioDurationMs),
    notes: typeof input.notes === 'string' ? input.notes : '',
  };
}

/**
 * Create a new immutable wirdRating doc. Returns the new doc id.
 * Each call writes a fresh doc; no overwrites (see §1.1).
 */
export async function saveWirdRating(payload) {
  const clean = normalizeWirdRating(payload);
  const ref = await addDoc(collection(db, WIRD_RATINGS), {
    ...clean,
    savedAt: serverTimestamp(),
  });
  // Fire-and-forget: never block a teacher's save on the detection path.
  maybeSuggestMemorization({ ...clean, id: ref.id }).catch((e) => {
    // eslint-disable-next-line no-console
    console.warn('memorization-suggestion hook failed:', e?.message || e);
  });
  // FSRS bridge is dormant — see `syncToReviewQueue` below. Every wird
  // save would otherwise fan out into a `reviewQueue` write, populating
  // Memorization Health and RevisionQueue. Keeping the helper so the
  // bridge can be re-enabled by un-commenting the call below.
  // syncToReviewQueue({ ...clean, id: ref.id }).catch((e) => {
  //   // eslint-disable-next-line no-console
  //   console.warn('reviewQueue sync failed:', e?.message || e);
  // });
  return ref.id;
}

/**
 * Bridge a saved wirdRating into the FSRS spaced-repetition engine. We
 * only act on rub'-grained saves (mode === 'rubuu'); custom verse
 * ranges have no portionId and don't fit the per-rub' card model.
 *
 * `kind === 'new'` is treated as the very first encounter — the card
 * is initialised then a Good/Easy review is recorded. `revision` /
 * `custom` (السرد / الماضي) become a single review on the existing card.
 *
 * Currently dormant — `saveWirdRating` does not call this. Kept here so
 * re-enabling FSRS is a single un-commented line.
 */
// eslint-disable-next-line no-unused-vars
async function syncToReviewQueue(saved) {
  if (!saved || !saved.studentId || !saved.portion) return;
  if (saved.portion.mode !== 'rubuu') return;
  const portionId = toPortionId(saved.portion.hizb, saved.portion.rubuu);
  if (!portionId) return;
  const quality = hifzScoreToQuality(saved.hifzScore);
  await srsService.recordReview(saved.studentId, portionId, quality, new Date());
}

/**
 * Post-save hook. If the just-saved rating was on a rub' (not a custom
 * verse range), re-scan this student's history for that rub' and upsert
 * a suggestion row when the threshold is met.
 *
 * Kept internal — called from saveWirdRating — so callers don't have to
 * know the promotion rules exist.
 */
async function maybeSuggestMemorization(justSaved) {
  if (!justSaved || !justSaved.portion) return;
  if (justSaved.portion.mode !== 'rubuu') return;
  const portionId = toPortionId(justSaved.portion.hizb, justSaved.portion.rubuu);
  if (!portionId) return;

  // Load the student's portion roster first — skip entirely if this
  // rub' is already flagged as memorised.
  let student;
  try {
    const sSnap = await getDoc(doc(db, 'students', justSaved.studentId));
    if (!sSnap.exists()) return;
    student = sSnap.data();
  } catch {
    return;
  }
  const already = new Set((student.memorizedPortions || []).map(Number));
  if (already.has(portionId)) return;

  // Tenant-level overrides — moudir can tighten or loosen the rules.
  // If auto-promotion is disabled at the tenant level, bail out.
  const tenantId = student.tenantId || justSaved.tenantId || null;
  const rules = await getHafizRules(tenantId);
  if (rules && rules.enabled === false) return;

  // Pull this student's ratings on this same rub' (cheap — bounded by
  // the teacher revisiting one rub' repeatedly, which is itself the
  // point of the whole signal).
  const q = query(
    collection(db, WIRD_RATINGS),
    where('studentId', '==', justSaved.studentId),
    where('portion.hizb', '==', justSaved.portion.hizb),
    where('portion.rubuu', '==', justSaved.portion.rubuu),
  );
  const snap = await getDocs(q);
  const ratings = snap.docs.map((d) => ({ id: d.id, ...d.data() }));

  const detections = detectMemorizationSuggestions({
    wirdRatings: ratings,
    memorizedPortions: student.memorizedPortions || [],
    thresholds: rules,
  });
  const match = detections.find((d) => d.portionId === portionId);
  if (!match) return;

  await upsertSuggestion({
    tenantId: student.tenantId || justSaved.tenantId || null,
    studentId: justSaved.studentId,
    classId: justSaved.classId || student.assignedClass || null,
    detection: match,
  });
}

export async function listWirdRatingsByStudent(studentId, { max = 200 } = {}) {
  const q = query(
    collection(db, WIRD_RATINGS),
    where('studentId', '==', studentId),
    orderBy('date', 'desc'),
    fsLimit(max),
  );
  const snap = await getDocs(q);
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}

/**
 * Director-dashboard helper: pull every wird saved in the tenant
 * between two dates inclusive. Filtered server-side by tenantId.
 */
export async function listWirdRatingsForTenant({ tenantId, fromDate, toDate, max = 5000 } = {}) {
  if (!tenantId) return [];
  const from = typeof fromDate === 'string'
    ? fromDate
    : fromDate?.toISOString?.().split('T')[0];
  const to = typeof toDate === 'string'
    ? toDate
    : toDate?.toISOString?.().split('T')[0];
  if (!from || !to) return [];
  try {
    const q = query(
      collection(db, WIRD_RATINGS),
      where('tenantId', '==', tenantId),
      where('date', '>=', from),
      where('date', '<=', to),
      orderBy('date', 'asc'),
      fsLimit(max),
    );
    const snap = await getDocs(q);
    return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn('listWirdRatingsForTenant failed:', e.message);
    return [];
  }
}

export async function listWirdRatingsByClass(classId, { max = 500 } = {}) {
  const q = query(
    collection(db, WIRD_RATINGS),
    where('classId', '==', classId),
    orderBy('date', 'desc'),
    fsLimit(max),
  );
  const snap = await getDocs(q);
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}

// Exposed for determinism / unit tests that need to construct a payload
// without hitting Firestore (see wirdRatingsService.test.js).
export const __test__ = {
  clampScore,
  normalizeVerses,
  normalizeRemarks,
  Timestamp,
};
